import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import defaults from 'lodash/defaults';
import omitBy from 'lodash/omitBy';
import isNull from 'lodash/isNull';
import { FiCheck } from 'react-icons/fi';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import dynamic from 'next/dynamic';
import {
  TypographyElement,
  useContentElement,
  useContentVisibility,
  VisibilityScheme,
} from '@plugins/next-cms-core';
import { ThemeProvider } from '@mui/material/styles';
import Container from '@components/atoms/Container';
import createFormTheme from '@components/molecules/form/formTheme';
import { useTranslation } from 'next-i18next';
import { useInView } from 'react-intersection-observer';
import { FORM_FIELDS } from '@components/molecules/form/fieldMapping';
import { useRouter } from 'next/router';

const Form = dynamic(() => import('../molecules/form/Form'), { ssr: false });

export default function FormBlock(props) {
  const { data } = props;
  const [ref, inView] = useInView();
  const [isFormVisible, setFormVisible] = useState(true);
  const [hasEnteredView, setHasEnteredView] = useState(false);
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { elementData } = useContentElement(
    data,
    FormBlock.dataSchema,
  );

  let title = null;
  const formAttributes = elementData.form?.data?.attributes;
  const isNewFormAllowed = elementData.form.postSubmitAction?.[0]?.isNewFormAllowed;

  if (elementData.title) {
    title = omitBy(elementData.title, isNull);
    defaults(title, {
      semanticVariant: 'h3',
      displayVariant: 'h3',
      textAlign: 'center',
    });
  }

  const rootStyles = useContentVisibility(elementData.visibility, {
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(5),
  });

  useEffect(() => {
    if (inView) setHasEnteredView(true);
  }, [inView]);

  return (
    // Nested so the redesigned input styling and the muted `text.secondary` reach the whole
    // block — heading, form and confirmation — without touching the global theme, which the
    // header search, vehicle filters and dialogs also render through.
    <ThemeProvider theme={createFormTheme}>
      <Box
        ref={ref}
        sx={rootStyles}
      >
        <Container>
          {(title || formAttributes?.subtitle) && (
          <Box mb={5}>
            {title && <TypographyElement data={title} />}
            {formAttributes?.subtitle && (
              <Typography
                color="text.secondary"
                sx={{
                  mt: 1,
                  textAlign: title?.textAlign ?? 'center',
                  whiteSpace: 'pre-line',
                }}
              >
                {formAttributes.subtitle}
              </Typography>
            )}
          </Box>
          )}
          {!isFormVisible && isNewFormAllowed && (
          <Box display="flex" justifyContent="center">
            <Button
              color="primary"
              onClick={() => setFormVisible(true)}
              size="small"
              variant="outlined"
            >
              {t('components.contentTypes.FormBlock.newRequest')}
            </Button>
          </Box>
          )}
          {!isFormVisible && !isNewFormAllowed && (
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.25,
              textAlign: 'center',
            }}
            variant="outlined"
          >
            {/* A filled mark rather than an outlined icon beside a heading: the point of
                this screen is that something finished, so the confirmation leads. */}
            <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              <FiCheck size={22} strokeWidth={3} />
            </Avatar>
            <Typography sx={{ fontWeight: 800 }} variant="h6">
              {t('components.contentTypes.FormBlock.thankYou')}
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 340 }} variant="body2">
              {t('components.contentTypes.FormBlock.requestReceived')}
            </Typography>
            <Button
              color="inherit"
              onClick={() => setFormVisible(!isFormVisible)}
              sx={{ mt: 0.75 }}
              variant="outlined"
            >
              {t('components.contentTypes.FormBlock.backToForm')}
            </Button>
          </Paper>
          )}
          {isFormVisible && elementData.form?.data && hasEnteredView && (
          <Form
            additionalValues={elementData.additionalValues ?? []}
            form={elementData.form.data.attributes}
            formId={elementData.form.data.id}
            queries={router.query}
            setFormVisible={setFormVisible}
          />
          )}
          {!elementData.form?.data && (
          <Alert severity="error">
            Formular konnte nicht geladen werden
          </Alert>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
}

FormBlock.typeName = 'ComponentContentForm'; // Strapi element type

FormBlock.propTypes = {
  data: PropTypes.shape({
    title: TypographyElement.propTypes,
    visibility: VisibilityScheme.propTypes,
    form: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  }).isRequired,
};

// Shared by the inline `fields` of a group and by the fields of a referenced field set,
// so both sides of `normalizeForm` always carry the exact same shape.
const FORM_FIELD_FRAGMENT = `
  id
  name
  label
  helperText
  isRequired
  isHidden
  isDisabled
  isReadOnly
  isConditional
  visibility
  matches
  rules {
    field
    compare
    value
    dataType
  }
  layout {
    width
  }
  ${Object.keys(FORM_FIELDS).map((type) => `
    ${FORM_FIELDS[type].graphQlSchema}
  `).join('')}
`;

FormBlock.graphQlSchema = `
... on ${FormBlock.typeName} {
  id
  title {
    ${TypographyElement.graphQlSchema}
  }
  visibility {
    ${VisibilityScheme.graphQlSchema}
  }
  additionalValues {
    key
    value
  }
  form {
    data {
      id
      attributes {
        name
        subtitle
        reassuranceText
        displayMode
        showProgress
        nextButtonLabel
        backButtonLabel
        sendButtonLabel
        recipientEmail
        recipientEmailTemplateId
        receiptEmailTemplateId
        sendReceiptEmailToIssuer
        items(pagination: {limit: 100}) {
          id
          title
          stepLabel
          autoAdvance
          isCollapsed
          isConditional
          visibility
          matches
          fieldSet {
            data {
              id
              attributes {
                name
                fields(pagination: {limit: 100}) {
                  ${FORM_FIELD_FRAGMENT}
                }
              }
            }
          }
          fields(pagination: {limit: 100}) {
            ${FORM_FIELD_FRAGMENT}
          }
          layout {
            width
          }
          rules {
            field
            compare
            value
            dataType
          }
        }
        to {
          email
          rules {
            field
            compare
            value
            dataType
          }
        }
        cc {
          email
          rules {
            field
            compare
            value
            dataType
          }
        }
        bcc {
          email
          rules {
            field
            compare
            value
            dataType
          }
        }
        postSubmitAction {
          ... on ComponentFormFormActionShowSuccessMessage {
            isNewFormAllowed
          }
          ... on ComponentFormFormActionRedirectToUrl {
            url
          }
        }
      }
    }
  }
}
`;
